import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, IconButton, Button, Divider, Stack, Grid, Chip, Box, Tooltip } from "@mui/material";
import { Close, ReceiptLong, History, Cancel, Visibility } from "@mui/icons-material";
import { format } from "date-fns";
import { IInstallmentProposal } from "../../../../../shared/services/api/proposal/ProposalService";
import { globalFormatCurrencyBRL, globalTranslatePayment, globalTranslateStatus } from "../../../../../shared/utils";

interface ModalInstallmentDetailsProps {
	open: boolean;
	onClose: () => void;
	row: IInstallmentProposal;
}

export const ModalInstallmentDetails = ({ open, onClose, row }: ModalInstallmentDetailsProps) => {
	const isCreditCard = row.paymentMethod?.toLowerCase().includes("card");

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					pb: 1,
				}}
			>
				<Typography fontWeight={400} variant="h6" color="primary.main">
					Detalhes da Parcela
				</Typography>
				<IconButton onClick={onClose}>
					<Close />
				</IconButton>
			</DialogTitle>

			<Divider />

			<DialogContent sx={{ px: 4, pt: 3, pb: 2 }}>
				<Stack spacing={2}>
					{/* Parcela nº + Valor */}
					<Stack direction="row" justifyContent="space-between" alignItems="center">
						<Box>
							<Typography variant="body2" color="text.secondary">
								Parcela Nº
							</Typography>
							<Typography variant="body1" fontWeight={600}>
								{row.number}
							</Typography>
						</Box>

						<Box textAlign="right">
							<Typography variant="body2" color="text.secondary">
								Valor
							</Typography>
							<Typography variant="h6" fontWeight={600} color="primary.main">
								{globalFormatCurrencyBRL(row.amount)}
							</Typography>
						</Box>
					</Stack>

					{/* Criação + Vencimento */}
					<Stack direction="row" justifyContent="space-between" alignItems="center">
						<Box>
							<Typography variant="body2" color="text.secondary">
								Criada em
							</Typography>
							<Typography variant="body1">{row.createdAt ? format(new Date(row.createdAt), "dd/MM/yyyy") : "-"}</Typography>
						</Box>

						<Box textAlign="right">
							<Typography variant="body2" color="text.secondary">
								Vencimento
							</Typography>
							<Typography variant="body1">{format(new Date(row.dueDate + " 03:00:00"), "dd/MM/yyyy")}</Typography>
						</Box>
					</Stack>

					{/* Tipo + Status */}
					<Grid container spacing={2}>
						<Grid item xs={6}>
							<Typography variant="body2" color="text.secondary">
								Tipo de Pagamento
							</Typography>
							<Typography variant="body1">{globalTranslatePayment(row.typeAmount)}</Typography>
						</Grid>

						<Grid item xs={6}>
							<Typography variant="body2" color="text.secondary">
								Status
							</Typography>
							<Chip
								size="small"
								icon={<Visibility />}
								label={globalTranslateStatus(row.status)}
								sx={{
									backgroundColor: "rgba(0,0,0,0.05)",
									color: "text.primary",
									fontWeight: 500,
								}}
							/>
						</Grid>
					</Grid>

					{/* Nota Fiscal */}
					{row.nfseId && (
						<Box>
							<Typography variant="body2" color="text.secondary">
								Nota Fiscal
							</Typography>
							<Chip size="small" icon={<ReceiptLong fontSize="small" />} label="Emitida" color="success" sx={{ fontWeight: 500 }} />
						</Box>
					)}

					{/* Motivo do cancelamento */}
					{row.status === "canceled" && row.reason && (
						<Box>
							<Typography variant="body2" color="text.secondary">
								Motivo do Cancelamento
							</Typography>
							<Typography variant="body1" color="error.main" fontStyle="italic">
								{row.reason}
							</Typography>
						</Box>
					)}

					{/* Observações com fundo leve */}
					{row.obs && (
						<Box
							sx={{
								backgroundColor: "#f5f5f5",
								borderRadius: 2,
								p: 2,
								mt: 1,
							}}
						>
							<Typography variant="subtitle2" color="text.secondary" gutterBottom>
								Observações
							</Typography>
							<Typography variant="body2">{row.obs}</Typography>
						</Box>
					)}
				</Stack>
			</DialogContent>

			<DialogActions sx={{ px: 4, pb: 3, pt: 1 }}>
				<Grid container justifyContent="space-between" alignItems="center">
					<Grid item>
						{isCreditCard && (
							<Tooltip title="Visualizar logs do pagamento">
								<Button variant="outlined" color="inherit" startIcon={<History />} onClick={() => console.log("Ver logs")}>
									Logs
								</Button>
							</Tooltip>
						)}
					</Grid>

					<Grid item>
						<Button variant="contained" color="primary" onClick={onClose}>
							Fechar
						</Button>
					</Grid>
				</Grid>
			</DialogActions>
		</Dialog>
	);
};
